import type { paths } from "@/api/types";
import { API_ORIGIN } from "@/shared/config";

import type {
  BodyOf,
  CursorPath,
  GetPath,
  IdempotentPath,
  Page,
  PatchPath,
  PathArgs,
  PostPath,
  QueryOf,
  Result,
} from "./envelope";
import { ApiError, NetworkError } from "./error";
import { getToken } from "./token";

/**
 * Единственная точка выхода в сеть.
 *
 * Базовый URL — origin или пустая строка. Префикс `/api/v1` сюда НЕ добавляется:
 * он уже входит в ключи `paths` сгенерированного контракта (блока `servers` в
 * openapi нет), и вторая копия дала бы `/api/v1/api/v1/home`.
 */

/** Публичные ручки. Токен к ним не прикладывается — он там просто не нужен. */
const PUBLIC_PATHS = new Set<string>(["/api/v1/auth/telegram", "/api/v1/legal/{slug}", "/api/v1/ping"]);

type Method = "GET" | "POST" | "PATCH";

interface RequestOptions {
  params?: Record<string, string | number> | undefined;
  query?: Record<string, unknown> | undefined;
  body?: unknown;
  idempotencyKey?: string | undefined;
  signal?: AbortSignal | undefined;
}

/**
 * Обработчик 401. Ставится модулем `auth`, чтобы клиент не зависел от него
 * напрямую — иначе получился бы цикл `client → auth → client`.
 */
type ReloginFn = () => Promise<boolean>;
let relogin: ReloginFn | undefined;

export function setReloginHandler(fn: ReloginFn): void {
  relogin = fn;
}

export function fillPath(template: string, params: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) {
      throw new Error(`В пути ${template} не передан параметр ${name}`);
    }
    return encodeURIComponent(String(value));
  });
}

function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // null/undefined не отправляем: бэкенд трактует присутствие параметра
    // иначе, чем его отсутствие (например, ?type= сузил бы выборку до пустой).
    if (value === undefined || value === null || value === "") continue;
    if (typeof value === "string") {
      search.append(key, value);
    } else if (typeof value === "number" || typeof value === "boolean") {
      search.append(key, String(value));
    } else {
      // Объект в query — всегда ошибка вызова: в контракте таких параметров
      // нет. Молчаливый `[object Object]` в URL искать потом дороже.
      throw new Error(`Параметр ${key} не примитив и не может уйти в query`);
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

async function request<T>(method: Method, template: string, options: RequestOptions): Promise<T> {
  const url = API_ORIGIN + fillPath(template, options.params) + buildQuery(options.query);

  const headers = new Headers();
  // FormData Content-Type НЕ ставим руками: браузер должен дописать к нему
  // boundary, а заданный вручную заголовок он не трогает — сервер получил бы
  // тело, которое нечем разобрать.
  const multipart = options.body instanceof FormData;
  if (options.body !== undefined && !multipart) {
    headers.set("Content-Type", "application/json");
  }
  if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);
  if (!PUBLIC_PATHS.has(template)) {
    const token = getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }

  const init: RequestInit = {
    method,
    headers,
    // credentials намеренно не включаем: в dev бэкенд отдаёт
    // `allow_origins: ["*"]` вместе с `allow_credentials: true`, и браузер
    // отвергает такой ответ на запрос с credentials. Токен ходит заголовком.
    ...(options.body !== undefined
      ? { body: multipart ? (options.body as FormData) : JSON.stringify(options.body) }
      : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  };

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (cause) {
    if (options.signal?.aborted) throw cause;
    throw new NetworkError(cause);
  }

  const requestId = response.headers.get("X-Request-ID") ?? undefined;

  if (response.ok) {
    return (await response.json()) as T;
  }

  throw await toApiError(response, requestId);
}

async function toApiError(response: Response, requestId: string | undefined): Promise<ApiError> {
  let code = "UNKNOWN_ERROR";
  let message = `HTTP ${String(response.status)}`;
  let details: Record<string, unknown> = {};

  try {
    const payload: unknown = await response.json();
    if (payload && typeof payload === "object" && "error" in payload) {
      const body = (payload as { error: { code?: string; message?: string; details?: unknown } }).error;
      code = body.code ?? code;
      message = body.message ?? message;
      if (body.details && typeof body.details === "object") {
        details = body.details as Record<string, unknown>;
      }
    }
  } catch {
    // Тело не разобралось — так выглядит 502 от nginx с HTML внутри.
    // Ошибка всё равно должна дойти до UI в понятном виде.
  }

  return new ApiError({ status: response.status, code, message, details, requestId });
}

/**
 * Один повтор после молчаливого релогина. Счётчик именно на запрос: без него
 * протухший токен и постоянный 401 закрутили бы бесконечный цикл логинов.
 */
async function withRelogin<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (!(error instanceof ApiError) || error.code !== "AUTH_ERROR" || !relogin) throw error;
    const ok = await relogin();
    if (!ok) throw error;
    return run();
  }
}

export function apiGet<K extends GetPath>(
  path: K,
  options: {
    params?: PathArgs<K>;
    query?: QueryOf<paths[K]["get"]>;
    signal?: AbortSignal;
  } = {},
): Promise<Result<paths[K]["get"]>> {
  return withRelogin(async () => {
    const body = await request<{ data: unknown }>("GET", path, options);
    return body.data as Result<paths[K]["get"]>;
  });
}

/**
 * Курсорная страница. Конверт снимается не до `data`, а до `{items, meta}`:
 * без `meta` нечем остановить `useInfiniteQuery`.
 */
export function apiGetPage<K extends CursorPath & GetPath>(
  path: K,
  options: {
    // Отзывы (доработки §6) — первая курсорная лента с параметром в пути:
    // `/robots/{robot_id}/comments`. `request` подстановку уже умеет, здесь
    // не хватало только пробросить параметр наружу.
    params?: PathArgs<K>;
    query?: QueryOf<paths[K]["get"]>;
    signal?: AbortSignal;
  } = {},
): Promise<Page<paths[K]["get"]>> {
  return withRelogin(async () => {
    const body = await request<{ data: unknown; meta?: unknown }>("GET", path, options);
    return {
      items: body.data as Page<paths[K]["get"]>["items"],
      meta: body.meta as Page<paths[K]["get"]>["meta"],
    };
  });
}

type PostOptions<K extends PostPath> = {
  params?: PathArgs<K>;
  body?: BodyOf<paths[K]["post"]>;
  signal?: AbortSignal;
} & (K extends IdempotentPath
  ? // Ключ обязателен на уровне типа: контракт помечает заголовок
    // необязательным, а бэкенд без него отвечает 400 REQUEST_ERROR.
    { idempotencyKey: string }
  : { idempotencyKey?: never });

export function apiPost<K extends PostPath>(
  path: K,
  options: PostOptions<K>,
): Promise<Result<paths[K]["post"]>> {
  return withRelogin(async () => {
    const body = await request<{ data: unknown }>("POST", path, options);
    return body.data as Result<paths[K]["post"]>;
  });
}

/**
 * Загрузка файла. Отдельно от `apiPost`, потому что тело у неё не из контракта:
 * в сгенерированных типах multipart-схема описана как объект с полем `file`
 * типа `string`, и подставить туда `File` не даст компилятор.
 */
export function apiUpload<T>(
  path: string,
  options: { params?: Record<string, string | number>; file: File; signal?: AbortSignal },
): Promise<T> {
  const form = new FormData();
  form.append("file", options.file);

  return withRelogin(async () => {
    const body = await request<{ data: unknown }>("POST", path, {
      ...(options.params ? { params: options.params } : {}),
      body: form,
      ...(options.signal ? { signal: options.signal } : {}),
    });
    return body.data as T;
  });
}

export function apiPatch<K extends PatchPath>(
  path: K,
  options: { params?: PathArgs<K>; body?: BodyOf<paths[K]["patch"]>; signal?: AbortSignal },
): Promise<Result<paths[K]["patch"]>> {
  return withRelogin(async () => {
    const body = await request<{ data: unknown }>("PATCH", path, options);
    return body.data as Result<paths[K]["patch"]>;
  });
}
